import '../styles.scss';
import { useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import { Button, Tooltip } from '@mui/material';
import Card from '../../../../common/card';
import Input from '../../../../common/input';
import SelectMenu from '../../../../common/select-menu';
import OnboardingDialog from '../../../../common/onboarding-dialog';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../../../config/routes';
import l from '../../../../../lang';
import { usePayouts } from '../../../../../store/payouts';
import { useHome } from '../../../../../store/home';
import { PayoutCreationStep, PurposeOfPayout, FlowType } from '../../../../../store/payouts/interfaces';
import { getPayoutWizardMenuItems, getPayoutCurrencyOptions, recordToSelectMenuOptions } from '../utils';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';

const PayoutData = () => {
  const history = useHistory();
  const { createPayout, setCreatePayoutData, setCreatePayoutStep, getPayoutFee, getPayoutCountryConfigurations } =
    usePayouts();
  const { balances, getBalances } = useHome();
  const beneficiaryName = String(createPayout.createPayoutData.beneficiaryFirstName || '-');
  const transferCountry = createPayout.createPayoutData.transferCountry || '';
  const flowType = createPayout.createPayoutData.flowType || FlowType.B2C;
  const currencyOptions = useMemo(
    () => getPayoutCurrencyOptions(createPayout.enabledCountryConfigurations.data, transferCountry, flowType),
    [createPayout.enabledCountryConfigurations.data, transferCountry, flowType]
  );
  const currencySelectOptions = useMemo(() => recordToSelectMenuOptions(currencyOptions), [currencyOptions]);

  useEffect(() => {
    if (createPayout.step < PayoutCreationStep.ACCOUNT_DATA) {
      history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA);
      return;
    }
    setCreatePayoutStep(PayoutCreationStep.PAYOUT_DATA);
    getBalances();
    if (!createPayout.enabledCountryConfigurations.data.length) {
      getPayoutCountryConfigurations();
    }
  }, []);

  useEffect(() => {
    const selectedCurrency = createPayout.createPayoutData.currencyToPay;
    if (selectedCurrency && !currencyOptions[selectedCurrency]) {
      setCreatePayoutData({ currencyToPay: undefined });
    }
  }, [currencyOptions, createPayout.createPayoutData.currencyToPay, setCreatePayoutData]);

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      bgColor="#F9FAFB"
      title={`${l('payouts.create.title')} | ${l('payouts.create.payoutData.menuTitle')}`}
      handleClose={() => history.push(Routes.PAYOUTS)}
      body={
        <div className="payout-create__layout">
          <Card>
            <div className="payout-create__card-content">
              <h2>{l('payouts.create.payoutData.title')}</h2>
              <br />
              <div className="transaction_detail__title_section">
                <BriefcaseIcon className="transaction_detail__title_section__icon" />
                {l('payouts.create.payoutData.title')}
              </div>
              <Formik
                initialValues={{
                  amount: createPayout.createPayoutData.transferAmount || '',
                  currency: createPayout.createPayoutData.currencyToPay || '',
                  description: createPayout.createPayoutData.description || '',
                  purpose: createPayout.createPayoutData.purpose || PurposeOfPayout.OTHER_SERVICES,
                }}
                enableReinitialize
                validate={values => {
                  const errors: Record<string, string> = {};
                  if (!values.amount || Number(values.amount) <= 0) {
                    errors.amount = l('form.input.required');
                  }
                  if (!values.currency) {
                    errors.currency = l('form.input.required');
                  }
                  if (!values.description) {
                    errors.description = l('form.input.required');
                  }
                  if (!values.purpose) {
                    errors.purpose = l('form.input.required');
                  }
                  return errors;
                }}
                onSubmit={values => {
                  const normalizedAmount = Number(values.amount);
                  setCreatePayoutData({
                    transferAmount: normalizedAmount,
                    currencyToPay: values.currency,
                    description: values.description,
                    purpose: values.purpose as PurposeOfPayout,
                  });
                  setCreatePayoutStep(PayoutCreationStep.REVIEW);
                  history.push(Routes.PAYOUTS_STEP_REVIEW);
                }}
              >
                {({ values, errors, touched, setFieldValue, handleBlur, handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="payout-create__payment-box">
                      <div>
                        <div className="payout-create__payment-box__label">{l('payouts.create.payoutData.payFrom')}</div>
                        <div className="payout-create__payment-box__value">
                          {balances.data?.availableBalance || 0} {balances.data?.currency || 'USD'}
                        </div>
                      </div>
                      <div>
                        <div className="payout-create__payment-box__label">{l('payouts.create.payoutData.payTo')}</div>
                        <div className="payout-create__payment-box__value">{beneficiaryName}</div>
                      </div>
                    </div>

                    <SelectMenu
                      name="currency"
                      label={l('payouts.create.field.currency')}
                      value={values.currency}
                      optionsArray={currencySelectOptions}
                      defaultOptionText={l('payouts.create.field.selectPlaceholder')}
                      error={touched.currency ? errors.currency : ''}
                      onBlur={handleBlur}
                      onChange={event => {
                        const newCurrency = event.target.value as string;
                        setFieldValue('currency', newCurrency);
                        if (values.amount) {
                          getPayoutFee(
                            Number(values.amount),
                            newCurrency,
                            createPayout.createPayoutData.transferCountry || ''
                          );
                        }
                      }}
                    />

                    <Input
                      type="number"
                      name="amount"
                      label={l('payouts.create.field.amount')}
                      value={values.amount}
                      error={touched.amount ? errors.amount : ''}
                      onBlur={handleBlur}
                      onChange={event => {
                        setFieldValue('amount', event.target.value);
                        if (event.target.value && values.currency) {
                          getPayoutFee(
                            Number(event.target.value),
                            values.currency,
                            createPayout.createPayoutData.transferCountry || ''
                          );
                        }
                      }}
                    />

                    <Input
                      name="description"
                      label={l('payouts.create.field.description')}
                      value={values.description}
                      error={touched.description ? errors.description : ''}
                      onBlur={handleBlur}
                      onChange={event => setFieldValue('description', event.target.value)}
                    />

                    <div className="payout-create__fee-row">
                      <div>
                        {l('payouts.create.payoutData.totalFees')}: {createPayout.fee.data?.feeAmount || 0}{' '}
                        {createPayout.fee.data?.feeCurrency || 'USD'}
                      </div>
                      <Tooltip title={l('payouts.create.payoutData.balanceTooltip')}>
                        <span className="payout-create__info">i</span>
                      </Tooltip>
                    </div>

                    <div className="payout-create__buttons">
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA)}
                        sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                      >
                        {l('common.cancel')}
                      </Button>
                      <Button variant="contained" size="large" type="submit">
                        {l('common.next')}
                      </Button>
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      }
      menuItems={getPayoutWizardMenuItems('payout')}
    />
  );
};

export default PayoutData;
